import { Box, IconButton, useTheme } from '@mui/material';
import { MinusCircle, PlusCircle } from 'lucide-react';
import React, { PropsWithChildren, useEffect, useMemo } from 'react';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';

import Divider from 'components/divider';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import TextField from 'components/text-field';

import Collapsible from 'components/collapsible';
import LoadingSpinner from 'components/loading-spinner';
import NumberField from 'components/number-field';
import { useAccountState } from 'contexts/account/account-context';
import { useParams } from 'react-router-dom';
import { MarketId } from 'types/market-id.enum';
import _ from 'underscore';
import { useOrderFeedState } from 'views/order-feed/context/order-feed-context';
import ProductAutocomplete from 'views/order-feed/features/my-supply-plan/components/product-autocomplete';
import SelectOrderDate from 'views/order-feed/features/my-supply-plan/components/select-order-date';
import { useMySupplyPlanState } from 'views/order-feed/features/my-supply-plan/context/my-supply-plan-context';
import OrderFormHeader from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/orders/form/order-header';
import { OFD2Params } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/ofd-2-params.type';
import { ActiveOrder } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/order-assortment.type';
import { calculateTotalOrderQuantity } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/utils/total-quantity-counter';
import { CreateOrderFormValues } from 'views/order-feed/features/my-supply-plan/types/create-order-form-values.type';
import { getOrderDateValueFromParts } from 'views/order-feed/features/my-supply-plan/utils/order-utils';

export interface DefaultFormValues {
  plantId?: string;
  vessel?: string;
  dispatchDate: string;
  siteErpId: string;
  orderLines?: {
    orderQuantity: number;
    itemNumber: string;
  }[];
}

type Props = PropsWithChildren & {
  onSubmit: any;
  showOrderDetails?: boolean;
  order?: ActiveOrder;
  defaultFormValues?: DefaultFormValues;
  forecastProductNames?: Record<string, string>;
};

const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan.orders';

const CreateOrderForm: React.FC<Props> = ({ order, onSubmit, children, defaultFormValues, showOrderDetails, forecastProductNames }) => {
  const intl = useIntl();
  const theme = useTheme();
  const { account } = useAccountState();
  const params = useParams<OFD2Params>();
  const {
    customerAssortmentQuery: { data: productAssortment, isFetching },
  } = useMySupplyPlanState();
  const {
    dispatchDatesQuery: { data: dispatchSchedule, isLoading: isDispatchScheduleLoading },
  } = useOrderFeedState();

  const isChileMarket = useMemo(() => {
    return account.company?.marketId === MarketId.Chile;
  }, [account]);

  const deliveryMethods = useMemo(() => {
    return account.company?.addressAccess.find((address) => address.id === params.globalSiteId)?.transportCompanies ?? [];
  }, [account.company]);

  const defaultValues: CreateOrderFormValues = useMemo(() => {
    let date = defaultFormValues?.dispatchDate || '';
    if (defaultFormValues?.plantId && defaultFormValues?.vessel) {
      date = getOrderDateValueFromParts(defaultFormValues.plantId, defaultFormValues.vessel, defaultFormValues.dispatchDate || '');
    }

    return {
      transportMethod: '',
      addressId: '',
      requestedDispatchDate: date,
      siteErpId: defaultFormValues?.siteErpId || '',
      orderComment: '',
      customerOrderReference: '',
      plantId: defaultFormValues?.plantId || '',
      vessel: defaultFormValues?.vessel || '',
      orderLines: defaultFormValues?.orderLines
        ? defaultFormValues?.orderLines?.map((line) => ({
            itemNumber: line.itemNumber,
            orderQuantity: line.orderQuantity,
          }))
        : [
            {
              itemNumber: '',
              orderQuantity: 0,
            },
          ],
    };
  }, [defaultFormValues]);

  //#region FORMS

  const { handleSubmit, control, reset, trigger, setValue, ...rest } = useForm<CreateOrderFormValues>({
    defaultValues: defaultValues,
  });

  const { fields, prepend, remove } = useFieldArray({
    control,
    name: 'orderLines',
  });

  const watchOrderLines = useWatch({
    control: control,
    name: 'orderLines',
    defaultValue: fields,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    const firstTransportMethod = _.first(deliveryMethods);

    if (!!firstTransportMethod) {
      setValue('transportMethod', firstTransportMethod.key);
      setValue('addressId', firstTransportMethod.name);
    }
  }, [account.company?.addressAccess, params.globalSiteId, deliveryMethods]);

  const totalOrderQuantityCalc = useMemo(() => {
    return calculateTotalOrderQuantity(watchOrderLines);
  }, [watchOrderLines, order]);

  //#endregion FORMS

  return (
    <FormProvider handleSubmit={handleSubmit} control={control} reset={reset} trigger={trigger} setValue={setValue} {...rest}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box gap={5} display={'flex'} flexDirection={'column'}>
          <OrderFormHeader value={totalOrderQuantityCalc} orderTitle={`${PAGE_NAME_ID}.totalOrderQuantity`} />
          <Box display={'flex'} gap={2}>
            <SelectOrderDate shippingSchedule={dispatchSchedule} isLoading={isDispatchScheduleLoading} />
            {deliveryMethods.length !== 1 && (
              <Select
                dark
                fullWidth
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.methodOfDelivery` })}
                controller={{
                  name: 'transportMethod',
                  control: control,
                  rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                }}>
                {account.company?.addressAccess
                  .find((address) => address.id === params.globalSiteId)
                  ?.transportCompanies.map((transport) => (
                    <RadioSelectItem key={transport.key} value={transport.key} onClick={() => setValue('addressId', transport.name)}>
                      {transport.key} - {transport.name}
                    </RadioSelectItem>
                  ))}
              </Select>
            )}
          </Box>
          <Divider dark />
          <Box display={'flex'} gap={2} flexDirection={'column'}>
            {isFetching ? (
              <Box maxHeight={40} width={'100%'} display={'flex'} justifyContent={'center'} alignItems={'center'} gap={4}>
                <Box display={'flex'}>
                  <LoadingSpinner />
                </Box>
                Loading products
              </Box>
            ) : (
              fields.map((field, idx) => (
                <Box display={'flex'} gap={2} key={field.id} width={'100%'}>
                  <ProductAutocomplete idx={idx} products={productAssortment} forecastProductNames={forecastProductNames} />
                  {!isFetching && (
                    <Box display={'flex'} justifyContent={'end'} alignItems={'start'}>
                      <NumberField
                        dark
                        label={'Quantity'}
                        disableDecimal
                        endAdornment={'kg'}
                        controller={{
                          name: `orderLines.${idx}.orderQuantity`,
                          control: control,
                          rules: {
                            required: intl.formatMessage({ id: 'form.rules.required' }),
                            validate: {
                              positive: (v) => (parseInt(v) >= 0 ? true : intl.formatMessage({ id: 'form.rules.positive' })),
                            },
                          },
                        }}
                      />
                      {idx !== 0 ? (
                        <>
                          <IconButton
                            color="primary"
                            title={intl.formatMessage({ id: `${PAGE_NAME_ID}.removeOrderLine` })}
                            onClick={() => remove(idx)}>
                            <MinusCircle size={46} strokeWidth={1} />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton
                            color="bluViu"
                            title={intl.formatMessage({ id: `${PAGE_NAME_ID}.addOrderLine` })}
                            onClick={() =>
                              prepend({
                                itemNumber: '',
                                orderQuantity: 0,
                              })
                            }>
                            <PlusCircle size={46} strokeWidth={1} />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              ))
            )}
          </Box>
          <Divider dark />
          {showOrderDetails && (
            <>
              <Collapsible
                backgroundColor={theme.palette.blue.o25}
                sx={{ borderRadius: 2 }}
                hideBorder
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.orderDetails` })}
                open={isChileMarket}>
                <Box display={'flex'} flexDirection={'column'} gap={4}>
                  <TextField
                    sx={{
                      backgroundColor: theme.palette.common.white,
                    }}
                    dark
                    fullWidth
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID}.customerReference` })}
                    maxLength={20}
                    controller={{
                      name: 'customerOrderReference',
                      control: control,
                    }}
                  />
                  <TextField
                    sx={{
                      backgroundColor: theme.palette.common.white,
                    }}
                    multiline
                    dark
                    fullWidth
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID}.orderComment` })}
                    controller={{
                      name: 'orderComment',
                      control: control,
                    }}
                  />
                </Box>
              </Collapsible>
            </>
          )}
          {children}
        </Box>
      </form>
    </FormProvider>
  );
};

export default CreateOrderForm;
